import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './Engine.module.css'
import { t } from 'i18next'
import { Button } from '../../common/components/button/Button'
import { ThreeDots } from '../../common/components/loaders/ThreeDots/ThreeDots'
import {
  BASE_TASK_COUNT,
  getThemesData,
  LearningGoal,
  READY_PROMPT,
  READY_PROMPTS,
  transformSnippets,
} from './EngineHelper'
import { TaskPreview } from '../../composites/TaskPreview/TaskPreview'
import { Task } from '../../types/commonTypes'
import { ModalPortal } from '../../common/components/Portal/Portal'
import { LearningGoals } from './components/LearningGoals'

interface Props {
  selectedGameBoardUrl: string | null
  gameBoardUrls: string[] | null
  previewRounds: string[] | null | undefined
  onUserPromt: (text: string) => void
  onCreateGame: () => void
  onRequestGameBoard: (text: string) => void
  onSelectGameBoard: (url: string) => void
  learningGoals: LearningGoal[]
  onAddLearningGoal: (goal: LearningGoal) => void
  onRemoveLearningGoal: (goal: LearningGoal) => void
  taskSlots: { [key: number]: Task }
  template?: string
  setShouldPoll: (shouldPoll: boolean) => void
  autoPrompt: string | null
  setAutoPrompt: (value: string | null) => void
  isPolling: boolean
  usage?: { prompt: number; completion: number; total: number }
  readyTasks: boolean
}

export const InteractiveStep: React.FC<Props> = ({
  selectedGameBoardUrl,
  gameBoardUrls,
  previewRounds,
  onUserPromt,
  onCreateGame,
  onRequestGameBoard,
  onSelectGameBoard,
  learningGoals,
  onAddLearningGoal,
  onRemoveLearningGoal,
  taskSlots,
  template,
  setShouldPoll,
  autoPrompt,
  setAutoPrompt,
  isPolling,
  usage,
  readyTasks,
}) => {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [userPrompts, setUserPrompts] = useState<string[]>([])
  const [imagePrompts, setImagePrompts] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [requestingGameBoard, setRequestingGameBoard] = useState(false)
  const createGameCalled = useRef(false)
  const isAutoPrompting = useRef(false)

  const taskCountOk = useMemo(() => {
    const themeTemplate = getThemesData().find((theme) => theme.value === template)
    return Object.keys(taskSlots).length >= (themeTemplate?.taskCount ?? BASE_TASK_COUNT)
  }, [taskSlots, template])

  const handleSelectGameBoard = useCallback(
    (url: string) => {
      onSelectGameBoard(url)
    },
    [onSelectGameBoard],
  )

  const startPolling = useCallback(() => {
    setTimeout(() => {
      setShouldPoll(true)
    }, 2000)
  }, [setShouldPoll])

  const handleSendPrompt = useCallback(
    (prompt?: string | any, displayText?: string) => {
      if (typeof prompt === 'string') {
        onUserPromt(prompt)
        setUserPrompts((prev) => prev.concat([displayText || prompt]))
        setLoading(true)
        startPolling()
        setAutoPrompt(null)
      } else if (inputRef.current) {
        onUserPromt(inputRef.current.value)
        setUserPrompts((prev) => prev.concat([inputRef.current ? inputRef.current.value : '']))
        inputRef.current.value = ''
        setLoading(true)
        startPolling()
      }
    },
    [onUserPromt, startPolling, setAutoPrompt],
  )

  // useEffect(() => {
  //   if (autoPrompt && !isAutoPrompting.current && !isPolling) {
  //     console.log('will auto prompt')
  //     setAutoPrompt(null)
  //     isAutoPrompting.current = true
  //     handleSendPrompt(autoPrompt)
  //   }
  // }, [autoPrompt, handleSendPrompt, isPolling, setAutoPrompt])

  useEffect(() => {
    if (!isPolling) {
      setTimeout(() => {
        isAutoPrompting.current = false
      }, 1000)
    }
  }, [isPolling])

  const handleCreateGame = useCallback(() => {
    onCreateGame()
  }, [onCreateGame])

  const handleRequestGameBoard = useCallback(
    (prompt?: string | any) => {
      if (typeof prompt === 'string' && prompt === 'INITIAL_VERSION') {
        onRequestGameBoard(taskSlots[1].subtasks[0].description.replace('<p>', '').replace('</p>', ''))
        setImagePrompts((prev) => prev.concat([taskSlots[1].subtasks[0].description]))
        startPolling()
      } else if (inputRef.current) {
        onRequestGameBoard(inputRef.current.value)
        setImagePrompts((prev) => prev.concat([inputRef.current ? inputRef.current.value : '']))
        inputRef.current.value = ''
        startPolling()
      }
      setRequestingGameBoard(true)
      setLoading(true)
    },
    [onRequestGameBoard, taskSlots, startPolling],
  )
  useEffect(() => {
    if (!requestingGameBoard && previewRounds && previewRounds.length > userPrompts.length) {
      setLoading(false)
    }
  }, [previewRounds, userPrompts, requestingGameBoard])

  useEffect(() => {
    if (gameBoardUrls && gameBoardUrls.length >= imagePrompts.length) {
      setLoading(false)
    }
  }, [gameBoardUrls, imagePrompts])

  useEffect(() => {
    if (selectedGameBoardUrl && !createGameCalled.current) {
      createGameCalled.current = true
      handleCreateGame()
    }
  }, [selectedGameBoardUrl, handleCreateGame])

  const hasSomeContent = useMemo(() => {
    return previewRounds && previewRounds[0] && previewRounds[0].length > 10
  }, [previewRounds])

  return (
    <div className={styles.twoCols}>
      <div className={styles.stepWrapper} style={{ minWidth: '300px', maxWidth: '450px' }}>
        <div className={styles.stepTitle}>{t('engine.customize.title', 'Customize your game')}</div>
        <div id='instructions_area' className={styles.instructionsArea}>
          <div style={{ display: 'flex', fontWeight: 'bold', alignItems: 'center', marginBottom: '16px' }}>
            <h3>{t('engine.customize.interactive_title', 'Review and fine tune the game')}</h3>
          </div>
          <div className={styles.instructionText}>
            {t(
              'engine.interactive.intro',
              'Review the initial game suggestion below and ask the AI to refine / modify if necessary. When you are happy with the content. Click on Create Game now',
            )}
          </div>
        </div>
        {!hasSomeContent && (
          <div className={styles.chatContainer}>
            {'Investigating your input(s). I will provide initial insights shortly...'}
          </div>
        )}
        {hasSomeContent &&
          previewRounds &&
          previewRounds.map((htmlContent, index) => {
            return (
              <>
                {index === 0 && learningGoals && learningGoals.length > 0 ? (
                  <div className={styles.chatContainer}>
                    {t(
                      'engine.info_about_learning_goals',
                      'The identified learning targets are editable on the section on the right. You can edit them until you proceed to task generation.',
                    )}
                  </div>
                ) : (
                  <div
                    className={styles.chatContainer}
                    dangerouslySetInnerHTML={{
                      __html: transformSnippets(htmlContent, index === 0 ? '---' : '<br'),
                    }}
                  />
                )}
                {userPrompts[index] && <div className={styles.chatContainer}>{userPrompts[index]}</div>}
              </>
            )
          })}
        {learningGoals && learningGoals.length > 0 && Object.values(taskSlots).length < 1 && (
          <div className={styles.readyPrompts}>
            <Button
              variant={'outline-tiny'}
              onClick={() =>
                handleSendPrompt(
                  READY_PROMPTS['GET_TASKS'].prompt +
                    learningGoals
                      .map((goal) => {
                        return goal.title + ': ' + goal.text + ' '
                      })
                      .join('\n'),
                  t(
                    'engine.display_getting_tasks_with_goals',
                    'Please provide the tasks with the focus on current learning goals.',
                  ),
                )
              }
            >
              {READY_PROMPTS['GET_TASKS'].title}
            </Button>
          </div>
        )}
        {gameBoardUrls?.map((url, index) => {
          return (
            <div className={styles.selectBoardArea}>
              {imagePrompts[index] && <div className={styles.chatContainer}>{imagePrompts[index]}</div>}
              {url.indexOf('engine_img') > -1 ? (
                <>
                  <img src={url} alt='' style={{ width: '500px' }} />
                  <Button
                    variant={'tiny'}
                    className={styles.selectBoardButton}
                    onClick={() => {
                      handleSelectGameBoard(url)
                    }}
                  >
                    {t('engine.interactive.select_game_board', 'Create game with this board')}
                  </Button>
                </>
              ) : (
                <div className={styles.chatContainer}>
                  {t('engine.interactive.select_game_board', 'Game board generation failed with this prompt')}
                </div>
              )}
            </div>
          )
        })}
        {(loading || !hasSomeContent) && <ThreeDots />}
        {requestingGameBoard && loading && (
          <div>{t('engine.interactive.generating_game_board', 'Generating game board image for preview.')}</div>
        )}
        {!requestingGameBoard && hasSomeContent && !autoPrompt && Object.values(taskSlots).length > 0 && !readyTasks && (
          <div className={styles.readyPrompts}>
            {Object.keys(READY_PROMPT)
              .filter(
                (k) =>
                  !(k.includes('MISSING_TASKS') && taskCountOk) &&
                  !READY_PROMPTS[k as keyof typeof READY_PROMPT].hideFromList,
              )
              .map((key) => {
                const readyPrompt = READY_PROMPTS[key as keyof typeof READY_PROMPT]
                return (
                  <Button
                    variant={'outline-tiny'}
                    onClick={() => handleSendPrompt(readyPrompt.prompt, readyPrompt.displayText)}
                  >
                    {READY_PROMPTS[key as keyof typeof READY_PROMPT].title}
                  </Button>
                )
              })}
          </div>
        )}
        {readyTasks && (
          <div className={styles.readyPrompts}>
            <Button
              variant={'outline-tiny'}
              onClick={() => handleSendPrompt(READY_PROMPTS['PROVIDE_MISSING_READY_TASKS'].prompt)}
            >
              {READY_PROMPTS['PROVIDE_MISSING_READY_TASKS'].title}
            </Button>
            <Button variant={'outline-tiny'} onClick={() => handleSendPrompt(READY_PROMPTS['FULL_READY_TASKS'].prompt)}>
              {READY_PROMPTS['FULL_READY_TASKS'].title}
            </Button>
          </div>
        )}
        {autoPrompt && (
          <div className={styles.readyPrompts}>
            {
              "It appears that some tasks can't be parsed to Seppo tasks currently, Would you like me to revise those tasks?"
            }
            <Button
              variant={'outline-tiny'}
              onClick={() =>
                handleSendPrompt(
                  READY_PROMPTS.CHECK_SPECIFIC_TASK_TYPES.prompt,
                  READY_PROMPTS.CHECK_SPECIFIC_TASK_TYPES.displayText,
                )
              }
            >
              {READY_PROMPTS.CHECK_SPECIFIC_TASK_TYPES.title}
            </Button>
          </div>
        )}
        {hasSomeContent && (
          <>
            <textarea
              placeholder={
                requestingGameBoard
                  ? t(
                      'engine.interactive.image_prompt',
                      'You can give your own prompt for the game board image generation',
                    )
                  : t(
                      'engine.interactive.your_own_prompt',
                      'Give your own guidance to the assistant, or choose a pre-defined prompt',
                    )
              }
              rows={5}
              ref={inputRef}
              className={styles.promptInput}
            />

            <Button variant={'big'} onClick={requestingGameBoard ? handleRequestGameBoard : handleSendPrompt}>
              {t('engine.interactive.send_prompt', 'Send')}
            </Button>
          </>
        )}
        {taskCountOk ? (
          <>
            {requestingGameBoard ? (
              <div>
                {t('engine.interactive.generating_game_board', 'Choose the game board image to create the game with.')}
              </div>
            ) : (
              <Button
                variant={'big'}
                onClick={
                  selectedGameBoardUrl
                    ? handleCreateGame
                    : () => {
                        handleRequestGameBoard('INITIAL_VERSION')
                      }
                }
              >
                {selectedGameBoardUrl
                  ? t('engine.interactive.create_game', 'Create Game Now')
                  : t('engine.interactive.request_gameboard', 'Continue to game board generation')}
              </Button>
            )}
          </>
        ) : (
          <div>
            {Object.values(taskSlots).length > 0
              ? t('engine.interactive.missing_tasks', 'We need more tasks to continue to game board generation')
              : ''}
          </div>
        )}
        {/*<ModalPortal>
        <div className={styles.exercisePreviewContainer}>
          {usage && (
            <div className={styles.tokenDisplay}>
              <div>{'Tokens'}</div>
              <div>{'prompt: ' + usage.prompt}</div>
              <div>{'completion: ' + usage.completion}</div>
              <div>{'total: ' + usage.total}</div>
            </div>
          )}
          <h3>{t('engine.interactive.tasks_preview', 'Tasks generated for your game')}</h3>
          <ul className={styles.exercisesList}>
            {Object.values(taskSlots).map((task) => {
              return (
                <TaskPreview
                  key={`task_${task.order}`}
                  task={{ ...task, name: task.order + '. ' + task.name }}
                  noPointsGame={false}
                  initialExpanded
                />
              )
            })}
          </ul>
        </div>
      </ModalPortal>*/}
      </div>
      <div>
        <div className={styles.exercisePreviewContainer}>
          {usage && (
            <div className={styles.tokenDisplay}>
              <div>{'Tokens'}</div>
              <div>{'prompt: ' + usage.prompt}</div>
              <div>{'completion: ' + usage.completion}</div>
              <div>{'total: ' + usage.total}</div>
            </div>
          )}
          {learningGoals && learningGoals.length > 0 && (
            <LearningGoals
              goals={learningGoals}
              onAddGoal={onAddLearningGoal}
              onDeleteGoal={onRemoveLearningGoal}
              editable={Object.values(taskSlots).length < 1}
            />
          )}
          <h3>{t('engine.interactive.tasks_preview', 'Tasks generated for your game')}</h3>
          <ul className={styles.exercisesList}>
            {Object.values(taskSlots).map((task) => {
              return (
                <TaskPreview
                  key={`task_${task.order}`}
                  task={{ ...task, name: task.order + '. ' + task.name }}
                  noPointsGame={false}
                  initialExpanded
                  wide
                />
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}
